import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Google as GoogleIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { googleCalendarService, GoogleCalendarEvent } from '../../services/googleCalendarService';

interface GoogleCalendarSyncDialogProps {
  open: boolean;
  onClose: () => void;
  events: Array<{
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    description?: string;
    location?: string;
    [key: string]: any;
  }>;
  selectedEventId?: string;
  organizationName: string;
}

const GoogleCalendarSyncDialog: React.FC<GoogleCalendarSyncDialogProps> = ({
  open,
  onClose,
  events,
  selectedEventId,
  organizationName
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{
    total: number;
    synced: number;
    failed: number;
  }>({ total: 0, synced: 0, failed: 0 });
  const [createNewCalendar, setCreateNewCalendar] = useState(true);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [syncComplete, setSyncComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize selected events if a specific event ID is provided
  useEffect(() => {
    if (selectedEventId) {
      setSelectedEvents([selectedEventId]);
    } else {
      setSelectedEvents(events.map(event => event.id));
    }
  }, [events, selectedEventId]);

  // Check authentication status on open
  useEffect(() => {
    if (open) {
      checkAuthStatus();
    }
  }, [open]);

  const checkAuthStatus = async () => {
    try {
      const isAuth = await googleCalendarService.checkAuthStatus();
      setIsAuthenticated(isAuth);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setError('Failed to check authentication status');
    }
  };

  const handleAuth = async () => {
    setIsAuthenticating(true);
    setError(null);
    
    try {
      const authUrl = await googleCalendarService.initiateAuth();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error during authentication:', error);
      setError('Failed to authenticate with Google');
      setIsAuthenticating(false);
    }
  };

  const handleSyncEvents = async () => {
    if (selectedEvents.length === 0) {
      setError('Please select at least one event to sync');
      return;
    }

    setIsSyncing(true);
    setSyncComplete(false);
    setError(null);

    const eventsToSync = events
      .filter(event => selectedEvents.includes(event.id))
      .map(event => ({
        summary: event.title,
        description: event.description || `Event from ${organizationName}`,
        location: event.location || '',
        startDateTime: event.start.toISOString(),
        endDateTime: event.end.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        allDay: event.allDay || false
      }));

    setSyncStatus({
      total: eventsToSync.length,
      synced: 0,
      failed: 0
    });

    try {
      let calendarId = 'primary';
      
      // Create a new calendar if requested
      if (createNewCalendar) {
        const calendarResponse = await googleCalendarService.createCalendar(
          `${organizationName} Events`
        );
        calendarId = calendarResponse.calendarId;
      }

      // Sync events to the selected/created calendar
      const syncResponse = await googleCalendarService.syncEventsToGoogleCalendar(eventsToSync);
      
      setSyncStatus({
        total: syncResponse.totalEvents,
        synced: syncResponse.successfulEvents,
        failed: syncResponse.failedEvents
      });
      
      setSyncComplete(true);
    } catch (error) {
      console.error('Error syncing events:', error);
      setError('Failed to sync events with Google Calendar');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSelectAllEvents = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedEvents(events.map(e => e.id));
    } else {
      setSelectedEvents([]);
    }
  };

  const handleSelectEvent = (eventId: string) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const handleClose = () => {
    if (!isSyncing) {
      setError(null);
      setSyncComplete(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="google-calendar-sync-dialog-title"
    >
      <DialogTitle id="google-calendar-sync-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <GoogleIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Google Calendar Sync</Typography>
          </Box>
          <IconButton aria-label="close" onClick={handleClose} disabled={isSyncing}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isAuthenticated ? (
          <Box textAlign="center" py={3}>
            <Typography variant="body1" paragraph>
              Connect your Google Calendar to sync events from {organizationName}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GoogleIcon />}
              onClick={handleAuth}
              disabled={isAuthenticating}
            >
              {isAuthenticating ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Connecting...
                </>
              ) : (
                'Connect Google Calendar'
              )}
            </Button>
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedEventId 
                  ? 'Sync this event with Google Calendar'
                  : 'Select events to sync with Google Calendar'}
              </Typography>
              
              {!selectedEventId && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedEvents.length === events.length}
                      onChange={handleSelectAllEvents}
                      disabled={isSyncing}
                    />
                  }
                  label="Select all events"
                />
              )}
            </Box>

            <List
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                bgcolor: 'background.paper'
              }}
            >
              {events
                .filter(event => !selectedEventId || event.id === selectedEventId)
                .map(event => (
                  <React.Fragment key={event.id}>
                    <ListItem>
                      <Checkbox
                        edge="start"
                        checked={selectedEvents.includes(event.id)}
                        onChange={() => handleSelectEvent(event.id)}
                        disabled={isSyncing}
                      />
                      <ListItemText
                        primary={event.title}
                        secondary={`${new Date(event.start).toLocaleString()} - ${new Date(event.end).toLocaleString()}`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
            </List>

            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createNewCalendar}
                    onChange={(e) => setCreateNewCalendar(e.target.checked)}
                    disabled={isSyncing}
                  />
                }
                label={`Create new calendar for ${organizationName}`}
              />
              <Typography variant="body2" color="textSecondary">
                {createNewCalendar
                  ? `A new calendar named "${organizationName} Events" will be created`
                  : 'Events will be added to your primary Google Calendar'}
              </Typography>
            </Box>

            {isSyncing && (
              <Box mt={2}>
                <Typography variant="body2" gutterBottom>
                  Syncing events... ({syncStatus.synced}/{syncStatus.total})
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(syncStatus.synced / syncStatus.total) * 100}
                />
              </Box>
            )}

            {syncComplete && (
              <Alert severity={syncStatus.failed > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
                {syncStatus.failed > 0
                  ? `Synced ${syncStatus.synced} of ${syncStatus.total} events. ${syncStatus.failed} failed.`
                  : `Successfully synced ${syncStatus.synced} events to Google Calendar!`}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSyncing}>
          {syncComplete ? 'Close' : 'Cancel'}
        </Button>
        {isAuthenticated && !syncComplete && (
          <Button
            onClick={handleSyncEvents}
            variant="contained"
            color="primary"
            disabled={isSyncing || selectedEvents.length === 0}
            startIcon={isSyncing ? <CircularProgress size={24} /> : <CalendarIcon />}
          >
            {isSyncing ? 'Syncing...' : 'Sync to Google Calendar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GoogleCalendarSyncDialog;
